export const MARKDOWN_HEADING_LEVELS = [1, 2, 3, 4, 5, 6] as const;
export type MarkdownHeadingLevel = (typeof MARKDOWN_HEADING_LEVELS)[number];
export type MarkdownHeadingSizes = Record<MarkdownHeadingLevel, number>;

export const MARKDOWN_HEADING_SIZE_STORAGE_KEY = "turbonotes2-markdown-heading-sizes";
export const MARKDOWN_BODY_SIZE_STORAGE_KEY = "turbonotes2-markdown-body-size";
export const MIN_MARKDOWN_HEADING_SIZE = 80;
export const MAX_MARKDOWN_HEADING_SIZE = 240;
export const MARKDOWN_HEADING_SIZE_STEP = 5;
export const MIN_MARKDOWN_BODY_SIZE = 14;
export const MAX_MARKDOWN_BODY_SIZE = 28;
export const MARKDOWN_BODY_SIZE_STEP = 1;
export const DEFAULT_MARKDOWN_BODY_SIZE = 20;

export const DEFAULT_MARKDOWN_HEADING_SIZES: Readonly<MarkdownHeadingSizes> = {
  1: 160,
  2: 135,
  3: 115,
  4: 105,
  5: 105,
  6: 105,
};

interface StorageReader {
  getItem(key: string): string | null;
}

export function isMarkdownHeadingLevel(value: number): value is MarkdownHeadingLevel {
  return MARKDOWN_HEADING_LEVELS.includes(value as MarkdownHeadingLevel);
}

export function defaultMarkdownHeadingSizes(): MarkdownHeadingSizes {
  return { ...DEFAULT_MARKDOWN_HEADING_SIZES };
}

export function normalizeMarkdownHeadingSize(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  const stepped = Math.round(value / MARKDOWN_HEADING_SIZE_STEP) * MARKDOWN_HEADING_SIZE_STEP;
  return Math.max(MIN_MARKDOWN_HEADING_SIZE, Math.min(MAX_MARKDOWN_HEADING_SIZE, stepped));
}

export function loadMarkdownHeadingSizes(storage: StorageReader): MarkdownHeadingSizes {
  const defaults = defaultMarkdownHeadingSizes();
  const stored = storage.getItem(MARKDOWN_HEADING_SIZE_STORAGE_KEY);
  if (!stored) return defaults;

  try {
    const parsed = JSON.parse(stored) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return defaults;
    const values = parsed as Record<string, unknown>;
    for (const level of MARKDOWN_HEADING_LEVELS) {
      defaults[level] = normalizeMarkdownHeadingSize(values[level], defaults[level]);
    }
    return defaults;
  } catch {
    return defaults;
  }
}

export function normalizeMarkdownBodySize(
  value: unknown,
  fallback = DEFAULT_MARKDOWN_BODY_SIZE,
): number {
  const numeric = typeof value === "string" && value.trim() !== ""
    ? Number(value)
    : value;
  if (typeof numeric !== "number" || !Number.isFinite(numeric)) return fallback;
  const stepped = Math.round(numeric / MARKDOWN_BODY_SIZE_STEP) * MARKDOWN_BODY_SIZE_STEP;
  return Math.max(MIN_MARKDOWN_BODY_SIZE, Math.min(MAX_MARKDOWN_BODY_SIZE, stepped));
}

export function loadMarkdownBodySize(storage: StorageReader): number {
  return normalizeMarkdownBodySize(storage.getItem(MARKDOWN_BODY_SIZE_STORAGE_KEY));
}

export function setMarkdownHeadingSize(
  sizes: MarkdownHeadingSizes,
  level: MarkdownHeadingLevel,
  value: number,
): MarkdownHeadingSizes {
  return {
    ...sizes,
    [level]: normalizeMarkdownHeadingSize(value, sizes[level]),
  };
}

export function markdownHeadingSizeCssValue(percentage: number): string {
  return `${percentage / 100}em`;
}

export function markdownBodySizeCssValue(size: number): string {
  return `${size}px`;
}
